export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_6d7e18ba-41a9-4763-a29d-007bdb63ffe4/artifacts/u1ut62ij_SGPL%20.png";

export const HERO_BG =
  "https://images.unsplash.com/photo-1684868265714-fd2300637c23?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";

export const PRODUCT_IMAGES = [
  "https://images.pexels.com/photos/15433841/pexels-photo-15433841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1601121141499-17ae80afc03a?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?crop=entropy&cs=srgb&fm=jpg&q=85",
];

export const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

// Backend base URL (CRA convention used on Emergent). Banner images are served
// from the backend's /uploads static mount, so we prefix relative paths with it.
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "";
export const bannerUrl = (path) => {
  if (!path) return HERO_BG;
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  return `${BACKEND_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

export const setupReveal = () => {
  if (typeof window === "undefined") return;

  // If the browser can't do IntersectionObserver, show everything rather than
  // leaving the page blank.
  if (!("IntersectionObserver" in window)) {
    document.querySelectorAll(".reveal").forEach((el) => el.classList.add("in-view"));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in-view"); }),
    { threshold: 0.12 }
  );

  const observe = (root) => {
    if (!root || root.nodeType !== 1) return;
    if (root.classList?.contains("reveal") && !root.classList.contains("in-view")) io.observe(root);
    root.querySelectorAll?.(".reveal:not(.in-view)").forEach((el) => io.observe(el));
  };

  observe(document.body);

  // Sections that mount AFTER this runs — products, banners, blogs and the
  // other async blocks — were never being observed, so they stayed at
  // opacity:0 forever. Watching the DOM catches them as they appear, which is
  // what made the home page render inconsistently.
  const mo = new MutationObserver((muts) => {
    muts.forEach((m) => m.addedNodes.forEach(observe));
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Safety net: nothing on the page should ever be permanently invisible. If
  // an element is still hidden after a moment, just show it.
  const failsafe = setTimeout(() => {
    document.querySelectorAll(".reveal:not(.in-view)").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 1.5) el.classList.add("in-view");
    });
  }, 1200);

  return () => { io.disconnect(); mo.disconnect(); clearTimeout(failsafe); };
};


export const WA_NUMBER = "919980187987"; // store WhatsApp click-to-chat
export const waLink = (msg = "Hello Swayambhu Gold, I'd like to know more.", phone = "") => {
  const target = (phone || WA_NUMBER).toString().replace(/\D/g, "");
  const withCc = target.length === 10 ? `91${target}` : target;
  return `https://wa.me/${withCc}?text=${encodeURIComponent(msg)}`;
};
// ---------- Showrooms ----------
// The two Swayambhu showrooms. VALUE = the exact name stored in the DB
// (stock rows, product.store and the inventory ledger all key on this string,
// so it must never be changed casually). LABEL = what staff see on screen.
export const CANONICAL_STORES = ["Swayambhu Gold", "Shri Mahaganapati Jewellers"];

const STORE_LABELS = {
  "Swayambhu Gold": "Swayambhu Gold, Belgaum",
  "Shri Mahaganapati Jewellers": "MG Jewellers, Khanapur",
};

/** Friendly display name for a store; unknown stores are shown as-is. */
export const storeLabel = (name) => STORE_LABELS[name] || name || "—";

// ---------- YouTube ----------
/**
 * Turn any YouTube link the client pastes into a valid /embed/ URL.
 * Accepts: youtu.be/ID · watch?v=ID · /embed/ID · /shorts/ID · /live/ID
 * or a bare 11-character video ID. Returns "" when nothing usable is found,
 * so callers can hide the player instead of rendering a broken iframe.
 */
export const ytEmbed = (raw, opts = {}) => {
  const v = (raw || "").trim();
  if (!v) return "";

  const extractId = (str) => {
    const patterns = [
      /youtu\.be\/([A-Za-z0-9_-]{11})/i,
      /[?&]v=([A-Za-z0-9_-]{11})/i,
      /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/i,
      /youtube\.com\/live\/([A-Za-z0-9_-]{11})/i,
      /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/i,
    ];
    for (const re of patterns) {
      const m = str.match(re);
      if (m) return m[1];
    }
    return /^[A-Za-z0-9_-]{11}$/.test(str) ? str : "";
  };

  const id = extractId(v);
  if (!id) return "";

  // Continuous playback: autoplay needs mute (browsers block sound-on
  // autoplay), and looping a single video needs playlist=<id> as well.
  if (opts.loop) {
    const params = new URLSearchParams({
      autoplay: "1",
      mute: "1",
      loop: "1",
      playlist: id,
      controls: opts.controls === false ? "0" : "1",
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
    });
    return `https://www.youtube.com/embed/${id}?${params.toString()}`;
  }

  // Already a full embed URL with its own params — leave it untouched.
  if (/youtube\.com\/embed\/[A-Za-z0-9_-]{11}\?/i.test(v)) return v;
  return `https://www.youtube.com/embed/${id}`;
};


// ---------- Gold rates ----------
// The client enters ONLY the 24K per-gram price (GST included).
// Every other carat is derived from it.
export const RATE_MULTIPLIERS = { gold_22k: 0.92, gold_20k: 0.84, gold_18k: 0.78 };

/** 22K / 20K / 18K per-gram prices derived from the 24K price. */
export const deriveRates = (gold24k) => {
  const base = Number(gold24k) || 0;
  return {
    gold_22k: Math.round(base * RATE_MULTIPLIERS.gold_22k * 100) / 100,
    gold_20k: Math.round(base * RATE_MULTIPLIERS.gold_20k * 100) / 100,
    gold_18k: Math.round(base * RATE_MULTIPLIERS.gold_18k * 100) / 100,
  };
};

// ---------- Gold exchange (old gold buy-back) ----------
// Buy-back purity. Deliberately different from the sell-side multipliers
// above — this is the fine-gold content we actually pay for.
//
// Raw gold (bars, coins, biscuits) is the ONLY form that reaches 100%.
// Ornaments are never 100%: solder, joints and stone settings mean an
// ornament always assays below its nominal carat.
export const EXCHANGE_PURITY_RAW = 100;
export const EXCHANGE_PURITY_ORNAMENT = { 22: 91, 20: 83, 18: 75 };

/** Buy-back purity %. Raw gold is 100%; ornaments use the carat table. */
export const exchangePurityFor = (karat, isRaw) =>
  isRaw ? EXCHANGE_PURITY_RAW : (EXCHANGE_PURITY_ORNAMENT[karat] ?? 0);

export const EXCHANGE_DEFAULTS = { gstPercent: 3, deductionPer10g: 1500 };

/**
 * Old-gold exchange value.
 *
 *   fine weight  = gross grams x purity%
 *   rate ex-GST  = 24K rate/g  /  (1 + gst%)      <- GST is INCLUDED, so divide
 *   net rate/g   = rate ex-GST  -  (deduction per 10g / 10)
 *   value        = net rate/g x fine weight
 *
 * Worked example: 23 g at 22K (91%) with 24K at Rs.15,000/g
 *   fine weight 20.93 g | net rate Rs.14,413.11/g | value Rs.3,01,666
 *
 * Every intermediate value is returned so the UI can show the full working —
 * the customer is handing over gold, so nothing should be hidden.
 */
export const calcExchange = (grossGrams, purityPercent, rate24k, opts = {}) => {
  const gstPercent = opts.gstPercent ?? EXCHANGE_DEFAULTS.gstPercent;
  const deductionPer10g = opts.deductionPer10g ?? EXCHANGE_DEFAULTS.deductionPer10g;

  const gross = Number(grossGrams) || 0;
  const pct = Number(purityPercent) || 0;
  const rate = Number(rate24k) || 0;

  const fineWeight = gross * (pct / 100);
  const rateExGst = rate / (1 + gstPercent / 100);
  const deductionPerGram = deductionPer10g / 10;
  const netRate = Math.max(rateExGst - deductionPerGram, 0);

  return {
    fineWeight,
    rateExGst,
    deductionPerGram,
    netRate,
    value: netRate * fineWeight,
  };
};
